import type { ReactElement } from 'react';
import type { BotDifficulty, LobbyPlayer } from '@big2/shared';
import { useI18n } from '../../i18n.js';
import { getUserAvatar } from '../../utils/avatar.js';
import { Icon } from '../Icon.js';
import { lobbySeatStats, type LobbyStatsContext } from './lobbySeatStats.js';

type FriendState = 'none' | 'friend' | 'pending';

interface LobbySeatDetailProps {
  readonly player: LobbyPlayer;
  readonly seatNumber: number;
  readonly isSelf: boolean;
  readonly botDifficulty?: BotDifficulty;
  readonly stats: LobbyStatsContext;
  readonly friendState: FriendState | null;
  readonly isMoving: boolean;
  readonly onClose: () => void;
  readonly onViewProfile?: () => void;
  readonly onAddFriend?: () => void;
  readonly onMove?: () => void;
  readonly onKick?: () => void;
}

const BOT_LEVEL = {
  easy: { en: 'Easy', mn: 'Хялбар' },
  medium: { en: 'Medium', mn: 'Дунд' },
  hard: { en: 'Hard', mn: 'Хүнд' },
} as const satisfies Record<BotDifficulty, { readonly en: string; readonly mn: string }>;

export function botLevelLabel(difficulty: BotDifficulty | undefined, isMn: boolean): string {
  const level = BOT_LEVEL[difficulty ?? 'medium'];
  return isMn ? level.mn : level.en;
}

/** Everything about one seated player, plus what you can do with them. Opens under the table. */
export function LobbySeatDetail({
  player, seatNumber, isSelf, botDifficulty, stats, friendState, isMoving,
  onClose, onViewProfile, onAddFriend, onMove, onKick,
}: LobbySeatDetailProps): ReactElement {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { tsai, points, delta } = lobbySeatStats(player, stats);
  const avatar = player.isBot ? null : getUserAvatar(player.userId || player.playerId, player.name);
  const isPaid = player.tier === 'paid';

  if (isMoving) {
    return (
      <div className="lobby-seat-detail is-moving" role="status">
        <p className="lobby-seat-detail-moving">
          {isMn ? `${player.name}-д шинэ суудал сонгоно уу` : `Tap a seat for ${player.name}`}
        </p>
        <button type="button" className="lobby-seat-detail-btn" onClick={onClose}>
          {isMn ? 'Болих' : 'Cancel'}
        </button>
      </div>
    );
  }

  return (
    <section className="lobby-seat-detail" aria-label={player.name}>
      <div className="lobby-seat-detail-head">
        <span
          className={`lobby-seat-avatar${player.isBot ? ' is-bot' : ''}${isPaid ? ' is-vip' : ''}`}
          style={avatar ? { background: avatar.gradient, color: '#ffffff' } : undefined}
          aria-hidden="true"
        >
          {avatar ? avatar.initial : '♠'}
        </span>
        <div className="lobby-seat-detail-who">
          <p className="lobby-seat-name">
            <span>{player.name}</span>
            {isPaid && (
              <span className="room-vip-tag" title="VIP"><Icon name="crown" size={12} /></span>
            )}
          </p>
          <p className="lobby-seat-meta">
            <span>{isMn ? `${seatNumber}-р суудал` : `Seat ${seatNumber}`}</span>
            {player.isHost && <span className="lobby-host-pill">♔ {isMn ? 'Эзэн' : 'Host'}</span>}
            {isSelf && <span className="lobby-you-tag">{isMn ? 'Та' : 'You'}</span>}
            {typeof player.level === 'number' && player.level > 0 && (
              <span className="level-badge">Lv.{player.level}</span>
            )}
            {player.isBot && <span>{isMn ? 'Бот' : 'Bot'}, {botLevelLabel(botDifficulty, isMn).toLowerCase()}</span>}
            {!player.isBot && !player.connected && <span className="lobby-offline-meta">{t.offline}</span>}
          </p>
        </div>
        <button
          type="button"
          className="lobby-seat-detail-close"
          onClick={onClose}
          aria-label={isMn ? 'Хаах' : 'Close'}
        >
          ✕
        </button>
      </div>

      {(tsai || points || delta) && (
        <div className="lobby-seat-badges">
          {tsai && (
            <span className={`lobby-tsai-badge${tsai.isLow ? ' is-low' : ''}`}>
              🍵 {tsai.balance} {isMn ? 'Цай' : 'Tsai'}
            </span>
          )}
          {points && (
            <span className={`lobby-point-badge is-${points.tone}`}>
              {points.limit ? `${points.total} / ${points.limit}` : points.total} {isMn ? 'оноо' : 'pts'}
            </span>
          )}
          {delta && (
            <span className={`lobby-score-delta is-${delta.tone}`}>
              {isMn ? 'Сүүлийн тойрог' : 'Last round'} {delta.text}
            </span>
          )}
        </div>
      )}

      <div className="lobby-seat-detail-actions">
        {onViewProfile && (
          <button type="button" className="lobby-seat-detail-btn lobby-self-profile-link" onClick={onViewProfile}>
            {isMn ? 'Профайл үзэх' : 'View profile'}
          </button>
        )}
        {friendState === 'friend' && <span className="lobby-friend-status">✓ {isMn ? 'Найз' : 'Friend'}</span>}
        {friendState === 'pending' && (
          <span className="lobby-friend-status is-pending">{isMn ? 'Хүсэлт илгээсэн' : 'Request sent'}</span>
        )}
        {friendState === 'none' && onAddFriend && (
          <button type="button" className="lobby-seat-detail-btn lobby-add-friend-btn" onClick={onAddFriend}>
            + {isMn ? 'Найз нэмэх' : 'Add friend'}
          </button>
        )}
        {onMove && (
          <button type="button" className="lobby-seat-detail-btn" onClick={onMove}>
            {isMn ? 'Суудал солих' : 'Move seat'}
          </button>
        )}
        {onKick && (
          <button type="button" className="lobby-kick-btn" onClick={onKick}>
            {isMn ? 'Өрөөнөөс гаргах' : 'Remove from room'}
          </button>
        )}
      </div>
    </section>
  );
}
